import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Octants from './pages/Octants';
import Galaxies from './pages/Galaxies';
import Quadrants from './pages/Quadrants';
import Sections from './pages/Sections';
import Systems from './pages/Systems';
import SystemView from './pages/SystemView';
import Explore3D from './pages/Explore3D';
import ElementsTable from './pages/ElementsTable';
import Admin from './pages/Admin';
import Inventory from './pages/Inventory';
import Missions from './pages/Missions';
import Discoveries from './pages/Discoveries';
import Base from './pages/Base';
import Factions from './pages/Factions';
import Story from './pages/Story';
import Fleet from './pages/Fleet';
import { Toaster } from './components/ui/sonner';
import './App.css';

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" />;
}

function App() {
  return (
    <AuthProvider>
      <div className="App">
        {/* Starfield background */}
        <div className="star-field">
          {[...Array(100)].map((_, i) => (
            <div
              key={i}
              className="star"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                width: `${Math.random() * 2 + 1}px`,
                height: `${Math.random() * 2 + 1}px`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${Math.random() * 3 + 2}s`
              }}
            />
          ))}
        </div>
        
        {/* Nebula overlay */}
        <div className="nebula-overlay"></div>
        
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/" element={<Navigate to="/explore/octants" />} />
            <Route
              path="/elements"
              element={
                <PrivateRoute>
                  <ElementsTable />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <PrivateRoute>
                  <Admin />
                </PrivateRoute>
              }
            />
            <Route
              path="/explore/octants"
              element={
                <PrivateRoute>
                  <Octants />
                </PrivateRoute>
              }
            />
            <Route
              path="/explore/galaxies/:octant"
              element={
                <PrivateRoute>
                  <Galaxies />
                </PrivateRoute>
              }
            />
            <Route
              path="/explore/quadrants/:octant/:galaxy"
              element={
                <PrivateRoute>
                  <Quadrants />
                </PrivateRoute>
              }
            />
            <Route
              path="/explore/sections/:octant/:galaxy/:quadrant"
              element={
                <PrivateRoute>
                  <Sections />
                </PrivateRoute>
              }
            />
            <Route
              path="/explore/systems/:octant/:galaxy/:quadrant/:section"
              element={
                <PrivateRoute>
                  <Systems />
                </PrivateRoute>
              }
            />
            <Route
              path="/system/:coords"
              element={
                <PrivateRoute>
                  <SystemView />
                </PrivateRoute>
              }
            />
            <Route
              path="/play/:coords/planet/:planetIdx"
              element={
                <PrivateRoute>
                  <Explore3D />
                </PrivateRoute>
              }
            />
            <Route
              path="/play/:coords/planet/:planetIdx/moon/:moonIdx"
              element={
                <PrivateRoute>
                  <Explore3D />
                </PrivateRoute>
              }
            />
            <Route
              path="/inventory"
              element={
                <PrivateRoute>
                  <Inventory />
                </PrivateRoute>
              }
            />
            <Route
              path="/missions"
              element={
                <PrivateRoute>
                  <Missions />
                </PrivateRoute>
              }
            />
            <Route
              path="/discoveries"
              element={
                <PrivateRoute>
                  <Discoveries />
                </PrivateRoute>
              }
            />
            <Route
              path="/base"
              element={
                <PrivateRoute>
                  <Base />
                </PrivateRoute>
              }
            />
            <Route
              path="/factions"
              element={
                <PrivateRoute>
                  <Factions />
                </PrivateRoute>
              }
            />
            <Route
              path="/story"
              element={
                <PrivateRoute>
                  <Story />
                </PrivateRoute>
              }
            />
            <Route
              path="/fleet"
              element={
                <PrivateRoute>
                  <Fleet />
                </PrivateRoute>
              }
            />
          </Routes>
        </Router>
        <Toaster richColors closeButton position="top-right" />
      </div>
    </AuthProvider>
  );
}

export default App;
